import { PermissionEnum } from '@Application/Security/Roles';
import { openFacilitySidebar } from '@UI/Pages/Agencies/Facilities/Signals/facilitySidebarSignal';
import { setFacilityToDelete } from '@UI/Pages/Agencies/Facilities/Signals/facilityToDeleteSignal';
import type FacilityView from '@UI/Store/Facilities/Views/FacilityView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-top: auto;
`;

type Props = {
	readonly facility: FacilityView;
};

/**
 * Leaf action component: it opens the edit sidebar and requests the delete confirmation purely
 * through their signals' setters — the page owns both overlays and reacts to the same signals.
 * Buttons carry visible text, rendered as `children` rather than `label`: the kit resolves the
 * accessible name from `label` first, which would shadow the explicit `aria-label` naming the
 * facility — `children` leaves that resolution to `aria-label`. The outer `div` is this card's
 * fourth subgrid track, not decorative filler: it stays outside `WithPermission` so it keeps
 * rendering — empty — for a caller without `FACILITY_WRITE`, instead of collapsing the track and
 * misaligning the card against its siblings.
 */
const FacilityCardActions: React.FC<Props> = ({ facility }) => {
	const handleEditClick = React.useCallback(() => {
		openFacilitySidebar(facility.id);
	}, [facility.id]);

	const handleDeleteClick = React.useCallback(() => {
		setFacilityToDelete(facility.id);
	}, [facility.id]);

	return (
		<div css={actionsRowStyle}>
			<WithPermission permission={PermissionEnum.FACILITY_WRITE}>
				<Button
					type="button"
					size="sm"
					aria-label={`Modifier l’agence ${facility.name}`}
					severity={'secondary'}
					onClick={handleEditClick}
				>
					Modifier
				</Button>
				<Button
					type="button"
					size="sm"
					aria-label={`Supprimer l’agence ${facility.name}`}
					severity={'danger'}
					onClick={handleDeleteClick}
				>
					Supprimer
				</Button>
			</WithPermission>
		</div>
	);
};

export default FacilityCardActions;
